// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Loading, useMedplum, useMedplumProfile } from '@medplum/react';
import { Suspense, lazy } from 'react';
import type { JSX } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { AppLayout } from './components/layout/AppLayout';
import { OrganizationGuard } from './components/OrganizationGuard';
import { hasDoseSpotIdentifier } from './components/utils';
import { AIRecordingProvider } from './contexts/AIRecordingContext';
import { CareCommProvider } from './contexts/CareCommContext';
import { AutoSaveProvider } from './contexts/AutoSaveContext';
import { MembershipProvider } from './contexts/MembershipContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { SessionTimeoutProvider } from './contexts/SessionTimeoutContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { ThemeColorProvider } from './contexts/ThemeColorContext';
import { TimezoneProvider } from './contexts/TimezoneContext';
import { FloatingRecorder } from './components/FloatingRecorder';
import { VideoCallOverlay, VideoCallPiP, IncomingCallAlert } from './components/carecomm';
import { UserPreferencesLoader } from './components/UserPreferencesLoader';
import { useAuditLoggerInit } from './hooks/useAuditLogger';
import { auditLogger } from './services/auditLogger';
import './index.css';

// Eagerly loaded pages (critical path - loaded on initial render)
import { SignInPage } from './pages/SignInPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { RegisterPage } from './pages/RegisterPage';

// Lazy loaded pages - Heavy pages with third-party dependencies (react-big-calendar, recharts)
const SchedulePage = lazy(() => import('./pages/schedule').then(m => ({ default: m.SchedulePage })));
const PracticeDashboardPage = lazy(() => import('./pages/PracticeDashboardPage').then(m => ({ default: m.PracticeDashboardPage })));
const ProviderDashboardPage = lazy(() => import('./pages/ProviderDashboardPage').then(m => ({ default: m.ProviderDashboardPage })));
const VitalsTab = lazy(() => import('./pages/patient/VitalsTab').then(m => ({ default: m.VitalsTab })));
const VitalsPage = lazy(() => import('./pages/patient/VitalsPage').then(m => ({ default: m.VitalsPage })));
const LabsPage = lazy(() => import('./pages/patient/LabsPage').then(m => ({ default: m.LabsPage })));
const AppointmentsPage = lazy(() => import('./pages/AppointmentsPage').then(m => ({ default: m.AppointmentsPage })));
const MessagesPage = lazy(() => import('./pages/messages/MessagesPage').then(m => ({ default: m.MessagesPage })));
const TasksPage = lazy(() => import('./pages/tasks/TasksPage').then(m => ({ default: m.TasksPage })));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage').then(m => ({ default: m.SettingsPage })));
const SpacesPage = lazy(() => import('./pages/spaces/SpacesPage').then(m => ({ default: m.SpacesPage })));
const DoseSpotFavoritesPage = lazy(() => import('./pages/integrations/DoseSpotFavoritesPage').then(m => ({ default: m.DoseSpotFavoritesPage })));
const StaffPage = lazy(() => import('./pages/staff/StaffPage').then(m => ({ default: m.StaffPage })));
const RegionalFeaturesPage = lazy(() => import('./pages/admin/RegionalFeaturesPage').then(m => ({ default: m.RegionalFeaturesPage })));

// Lazy loaded pages - Medium weight pages
const PatientsListPage = lazy(() => import('./pages/PatientsListPage').then(m => ({ default: m.PatientsListPage })));
const PatientCreatePage = lazy(() => import('./pages/patient/PatientCreatePage').then(m => ({ default: m.PatientCreatePage })));
const EncounterChartPage = lazy(() => import('./pages/encounter/EncounterChartPage').then(m => ({ default: m.EncounterChartPage })));
const EncounterModal = lazy(() => import('./pages/encounter/EncounterModal').then(m => ({ default: m.EncounterModal })));
const PatientPage = lazy(() => import('./pages/patient/PatientPage').then(m => ({ default: m.PatientPage })));
const IntakeFormPage = lazy(() => import('./pages/patient/IntakeFormPage').then(m => ({ default: m.IntakeFormPage })));
const ServicesPage = lazy(() => import('./pages/ServicesPage').then(m => ({ default: m.ServicesPage })));
const SearchPage = lazy(() => import('./pages/SearchPage').then(m => ({ default: m.SearchPage })));

// Lazy loaded patient tabs
const CommunicationTab = lazy(() => import('./pages/patient/CommunicationTab').then(m => ({ default: m.CommunicationTab })));
const DoseSpotTab = lazy(() => import('./pages/patient/DoseSpotTab').then(m => ({ default: m.DoseSpotTab })));
const EditTab = lazy(() => import('./pages/patient/EditTab').then(m => ({ default: m.EditTab })));
const ExportTab = lazy(() => import('./pages/patient/ExportTab').then(m => ({ default: m.ExportTab })));
const PatientSearchPage = lazy(() => import('./pages/patient/PatientSearchPage').then(m => ({ default: m.PatientSearchPage })));
const TimelineTab = lazy(() => import('./pages/patient/TimelineTab').then(m => ({ default: m.TimelineTab })));
const FacesheetTab = lazy(() => import('./pages/patient/FacesheetTab').then(m => ({ default: m.FacesheetTab })));
const EncountersTab = lazy(() => import('./pages/patient/EncountersTab').then(m => ({ default: m.EncountersTab })));
const MedicationsTab = lazy(() => import('./pages/patient/MedicationsTab').then(m => ({ default: m.MedicationsTab })));
const CarePlansTab = lazy(() => import('./pages/patient/CarePlansTab').then(m => ({ default: m.CarePlansTab })));
const TasksTab = lazy(() => import('./pages/patient/TasksTab').then(m => ({ default: m.TasksTab })));
const MedicationRequestPage = lazy(() => import('./pages/patient/MedicationRequestPage').then(m => ({ default: m.MedicationRequestPage })));
const CarePlanPage = lazy(() => import('./pages/patient/CarePlanPage').then(m => ({ default: m.CarePlanPage })));
const TaskPage = lazy(() => import('./pages/patient/TaskPage').then(m => ({ default: m.TaskPage })));

// Lazy loaded resource pages
const ResourceCreatePage = lazy(() => import('./pages/resource/ResourceCreatePage').then(m => ({ default: m.ResourceCreatePage })));
const ConditionCreatePage = lazy(() => import('./pages/condition/ConditionCreatePage').then(m => ({ default: m.ConditionCreatePage })));
const AllergyIntoleranceCreatePage = lazy(() => import('./pages/allergy/AllergyIntoleranceCreatePage').then(m => ({ default: m.AllergyIntoleranceCreatePage })));
const ObservationCreatePage = lazy(() => import('./pages/observation/ObservationCreatePage').then(m => ({ default: m.ObservationCreatePage })));
const LabResultCreatePage = lazy(() => import('./pages/labs/LabResultCreatePage').then(m => ({ default: m.LabResultCreatePage })));
const ProcedureCreatePage = lazy(() => import('./pages/procedure/ProcedureCreatePage').then(m => ({ default: m.ProcedureCreatePage })));
const CoverageCreatePage = lazy(() => import('./pages/coverage/CoverageCreatePage').then(m => ({ default: m.CoverageCreatePage })));
const ResourceDetailPage = lazy(() => import('./pages/resource/ResourceDetailPage').then(m => ({ default: m.ResourceDetailPage })));
const ResourceEditPage = lazy(() => import('./pages/resource/ResourceEditPage').then(m => ({ default: m.ResourceEditPage })));
const ResourceHistoryPage = lazy(() => import('./pages/resource/ResourceHistoryPage').then(m => ({ default: m.ResourceHistoryPage })));
const ResourcePage = lazy(() => import('./pages/resource/ResourcePage').then(m => ({ default: m.ResourcePage })));

// Lazy loaded components
const TaskDetailsModal = lazy(() => import('./components/tasks/TaskDetailsModal').then(m => ({ default: m.TaskDetailsModal })));

/**
 * Inner component that initializes the audit logger
 * Must be inside OrganizationProvider to access organization context
 */
function AuditLoggerInitializer(): null {
  useAuditLoggerInit();
  return null;
}

/**
 * Callback for session timeout - logs the event before logout
 */
function handleSessionExpired(): void {
  // Log the session timeout event before Medplum signs out
  auditLogger.logSessionTimeout(15); // Default 15 minutes
}

export function App(): JSX.Element | null {
  const medplum = useMedplum();
  const profile = useMedplumProfile();

  if (medplum.isLoading()) {
    return null;
  }

  const membership = medplum.getProjectMembership();
  const hasDoseSpot = hasDoseSpotIdentifier(membership);

  // Show sign in page without the app layout
  if (!profile) {
    return (
      <Routes>
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="*" element={<Navigate to="/signin" replace />} />
      </Routes>
    );
  }

  return (
    <MembershipProvider>
    <SessionTimeoutProvider onSessionExpired={handleSessionExpired}>
      <AutoSaveProvider>
        <SidebarProvider>
          <ThemeColorProvider>
            <UserPreferencesLoader />
            <OrganizationProvider>
              <CareCommProvider>
              <AIRecordingProvider>
              <TimezoneProvider>
              <OrganizationGuard>
              <AuditLoggerInitializer />
              <FloatingRecorder />
              <VideoCallOverlay />
              <VideoCallPiP />
              {/* MinimizedCallIndicator is now integrated into AppLayout as AppShell.Footer */}
              <IncomingCallAlert />
              <AppLayout>
              <Suspense fallback={<Loading />}>
                <Routes>
                <Route path="/spaces" element={<SpacesPage />} />
                <Route path="/provider" element={<ProviderDashboardPage />} />
                <Route path="/practice" element={<PracticeDashboardPage />} />
                <Route path="/dashboard" element={<Navigate to="/provider" replace />} />
                <Route path="/" element={<Navigate to="/provider" replace />} />
                <Route path="/patients" element={<PatientsListPage />} />
                <Route path="/Patient/new" element={<PatientCreatePage />} />
                <Route path="/Patient/:patientId" element={<PatientPage />}>
                  <Route path="Encounter/new" element={<EncounterModal />} />
                  <Route path="Encounter/:encounterId" element={<EncounterChartPage />}>
                    <Route path="Task/:taskId" element={<TaskDetailsModal />} />
                  </Route>
                  <Route path="edit" element={<EditTab />} />
                  <Route path="Message" element={<CommunicationTab />} />
                  <Route path="Message/:messageId" element={<CommunicationTab />} />
                  {hasDoseSpot && <Route path="dosespot" element={<DoseSpotTab />} />}
                  <Route path="timeline" element={<TimelineTab />} />
                  <Route path="export" element={<ExportTab />} />
                  <Route path="labs" element={<LabsPage />} />
                  <Route path="labs/:labId" element={<LabsPage />} />
                  <Route path="vitals" element={<VitalsTab />} />
                  <Route path="vitals/new" element={<VitalsPage />} />
                  <Route path="visits" element={<EncountersTab />} />
                  <Route path="meds" element={<MedicationsTab />} />
                  <Route path="careplans" element={<CarePlansTab />} />
                  <Route path="tasks" element={<TasksTab />} />
                  <Route path="MedicationRequest/new" element={<MedicationRequestPage />} />
                  <Route path="MedicationRequest/:id" element={<MedicationRequestPage />} />
                  <Route path="CarePlan/new" element={<CarePlanPage />} />
                  <Route path="CarePlan/:id" element={<CarePlanPage />} />
                  <Route path="Task/new" element={<TaskPage />} />
                  <Route path="Task/:id" element={<TaskPage />} />
                  <Route path="Condition/new" element={<ConditionCreatePage />} />
                  <Route path="AllergyIntolerance/new" element={<AllergyIntoleranceCreatePage />} />
                  <Route path="Observation/new" element={<ObservationCreatePage />} />
                  <Route path="lab-result/new" element={<LabResultCreatePage />} />
                  <Route path="Procedure/new" element={<ProcedureCreatePage />} />
                  <Route path="Coverage/new" element={<CoverageCreatePage />} />
                  <Route path=":resourceType" element={<PatientSearchPage />} />
                  <Route path=":resourceType/new" element={<ResourceCreatePage />} />
                  <Route path=":resourceType/:id" element={<ResourcePage />}>
                    <Route path="" element={<ResourceDetailPage />} />
                    <Route path="edit" element={<ResourceEditPage />} />
                    <Route path="history" element={<ResourceHistoryPage />} />
                  </Route>
                  <Route path="" element={<FacesheetTab />} />
                </Route>
                <Route path="/Message" element={<MessagesPage />}>
                  <Route index element={<MessagesPage />} />
                  <Route path=":messageId" element={<MessagesPage />} />
                </Route>
                <Route path="Task" element={<TasksPage />} />
                <Route path="Task/:taskId" element={<TasksPage />} />
                <Route path="/onboarding" element={<IntakeFormPage />} />
                <Route path="/schedule" element={<SchedulePage />} />
                <Route path="/staff" element={<StaffPage />} />
                <Route path="/admin/features" element={<RegionalFeaturesPage />} />
                <Route path="/signin" element={<Navigate to="/" replace />} />
                <Route path="/dosespot" element={<DoseSpotTab />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/appointments" element={<AppointmentsPage />} />
                <Route path="/:resourceType" element={<SearchPage />} />
                <Route path="/:resourceType/new" element={<ResourceCreatePage />} />
                <Route path="/:resourceType/:id" element={<ResourcePage />}>
                  <Route path="" element={<ResourceDetailPage />} />
                  <Route path="edit" element={<ResourceEditPage />} />
                  <Route path="history" element={<ResourceHistoryPage />} />
                </Route>
                {hasDoseSpot && <Route path="/integrations/dosespot" element={<DoseSpotFavoritesPage />} />}
                </Routes>
              </Suspense>
            </AppLayout>
              </OrganizationGuard>
              </TimezoneProvider>
              </AIRecordingProvider>
              </CareCommProvider>
            </OrganizationProvider>
          </ThemeColorProvider>
        </SidebarProvider>
      </AutoSaveProvider>
    </SessionTimeoutProvider>
    </MembershipProvider>
  );
}
